import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material'
import {
  Visibility,
  Code,
  Save,
  Refresh,
  Help,
  AutoAwesome,
  SmartToy
} from '@mui/icons-material'
import mermaid from 'mermaid'
import axios from 'axios'

// Inicializar Mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Arial, sans-serif'
})

const DiagramEditor = ({
  initialCode = '',
  onSave,
  onChange = null, // Callback para notificar cambios sin guardar
  readOnly = false,
  actividades = [],
  procedimientoId = null,
  procedimientoData = null // Para datos del procedimiento en creación
}) => {
  const [mermaidCode, setMermaidCode] = useState(initialCode)
  const [svgContent, setSvgContent] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [showStatusBar, setShowStatusBar] = useState(false)
  const [aiAttempts, setAiAttempts] = useState(0)
  const [maxAiAttempts] = useState(10)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const diagramRef = useRef(null)

  // Generar clave única para este procedimiento en esta sesión
  const sessionKey = `aiAttempts_${procedimientoId || 'new'}_${procedimientoData?.titulo || 'untitled'}`

  // Cargar intentos existentes al montar el componente
  useEffect(() => {
    const savedAttempts = parseInt(sessionStorage.getItem(sessionKey) || '0')
    setAiAttempts(savedAttempts)
  }, [sessionKey])

  useEffect(() => {
    if (mermaidCode) {
      renderDiagram()
    }
  }, [])

  // Debug: log de actividades
  useEffect(() => {
    console.log('🔍 DiagramEditor - Estado de actividades:', {
      cantidad: actividades.length,
      procedimientoId: procedimientoId,
      procedimientoData: procedimientoData,
      actividades: actividades,
      showAIButton: actividades.length > 0,
      timestamp: new Date().toISOString()
    })
  }, [actividades, procedimientoId, procedimientoData])

  // Función para mostrar mensajes de estado
  const updateStatusMessage = (message) => {
    setStatusMessage(message)
    setShowStatusBar(true)
    console.log('📊 Status:', message)
  }

  // Función para ocultar la barra de estado
  const hideStatusBar = () => {
    setShowStatusBar(false)
    setStatusMessage('')
  }

  // Función para incrementar intentos de IA
  const incrementAiAttempts = () => {
    const newAttempts = aiAttempts + 1
    setAiAttempts(newAttempts)
    sessionStorage.setItem(sessionKey, newAttempts.toString())
    
    if (newAttempts >= maxAiAttempts) {
      updateStatusMessage(`⚠️ Límite de intentos alcanzado: ${maxAiAttempts} intentos por sesión`)
    }
  }

  // Actualizar el código cuando cambie el prop initialCode
  useEffect(() => {
    if (initialCode !== mermaidCode) {
      setMermaidCode(initialCode)
    }
  }, [initialCode])

  // Auto-renderizar cuando cambie el código Mermaid (especialmente después de generar con IA)
  useEffect(() => {
    if (mermaidCode && mermaidCode.trim()) {
      const timeout = setTimeout(() => {
        renderDiagram()
      }, 200) // Pequeño delay para asegurar que el estado se actualice
      
      return () => clearTimeout(timeout)
    }
  }, [mermaidCode])

  const renderDiagram = async () => {
    if (!mermaidCode.trim()) {
      setSvgContent('')
      setError('')
      return
    }

    setLoading(true)
    setError('')

    try {
      const diagramId = `mermaid-${Date.now()}`
      const { svg } = await mermaid.render(diagramId, mermaidCode)
      setSvgContent(svg)

      // Notificar que hay cambios sin guardar
      if (onChange) {
        onChange(mermaidCode, svg, true) // true = tiene cambios sin guardar
      }
    } catch (err) {
      console.error('Error renderizando diagrama:', err)
      setError('Error en la sintaxis del diagrama. Verifique el código Mermaid.')
      setSvgContent('')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (value) => {
    setMermaidCode(value)
    setError('')
  }

  const handleSave = () => {
    if (onSave) {
      onSave(mermaidCode, svgContent)
    }

    // Notificar que ya no hay cambios sin guardar
    if (onChange) {
      onChange(mermaidCode, svgContent, false) // false = sin cambios sin guardar
    }

    // Mostrar mensaje de éxito local
    setSaveSuccess(true)
    setTimeout(() => setSaveSuccess(false), 3000)
  }

  const generateBasicDiagram = () => {
    if (actividades.length === 0) {
      setMermaidCode(`flowchart TD
    A[INICIO] --> B[Actividad 1]
    B --> C[Actividad 2]
    C --> D[FIN]`)
      return
    }

    const steps = actividades
      .sort((a, b) => a.orden - b.orden)
      .map((actividad, index) => {
        const nodeId = String.fromCharCode(65 + index) // A, B, C...
        const description = actividad.descripcion.substring(0, 50) + (actividad.descripcion.length > 50 ? '...' : '')
        return {
          id: nodeId,
          text: description.replace(/\n/g, '<br/>')
        }
      })

    let diagramCode = 'flowchart TD\n    START[INICIO]'
    
    steps.forEach((step, index) => {
      const prevNode = index === 0 ? 'START' : steps[index - 1].id
      diagramCode += ` --> ${step.id}[${step.text}]`
      if (index === 0) diagramCode += `\n    ${prevNode}`
    })

    diagramCode += ` --> END[FIN]`

    setMermaidCode(diagramCode)
  }

  // Función inteligente que decide qué método de IA usar
  const generateWithSmartAI = async () => {
    if (procedimientoId) {
      // Si tenemos procedimientoId, usar el método estándar que funciona
      console.log('🔄 Usando método AI estándar (procedimiento existente)')
      await generateWithAI()
    } else {
      // Si no tenemos procedimientoId, crear uno temporal y usar el mismo endpoint que funciona
      console.log('🔄 Creando procedimiento temporal para usar el mismo endpoint que funciona...')

      if (actividades.length === 0) {
        setError('No hay actividades para generar el diagrama. Agregue actividades primero.')
        return
      }

      // Verificar límite de intentos antes de crear procedimiento temporal
      if (aiAttempts >= maxAiAttempts) {
        setError(`Has alcanzado el límite de ${maxAiAttempts} intentos de generación con IA para este procedimiento en esta sesión.`)
        updateStatusMessage(`⚠️ Límite alcanzado: ${aiAttempts}/${maxAiAttempts} intentos`)
        return
      }

      setAiLoading(true)
      incrementAiAttempts()
      updateStatusMessage(`🤖 Creando procedimiento temporal y generando diagrama (${aiAttempts + 1}/${maxAiAttempts})...`)

      try {
        // Validar que tenemos los datos mínimos necesarios
        const titulo = procedimientoData?.titulo?.trim() || 'Procedimiento Temporal para Diagrama IA'
        const objetivo = procedimientoData?.objetivo?.trim() || 'Generar diagrama de flujo usando inteligencia artificial para visualizar el proceso'
        const alcance = procedimientoData?.alcance?.trim() || 'Aplicable temporalmente para la generación de diagramas con IA'
        const procesoNombre = procedimientoData?.proceso?.nombre || procedimientoData?.proceso || 'Proceso Temporal para IA'
        const gestionId = procedimientoData?.gestion_id || 1

        // Asegurar longitudes mínimas
        const tituloValido = titulo.length >= 5 ? titulo : 'Procedimiento Temporal para Diagrama IA'
        const objetivoValido = objetivo.length >= 10 ? objetivo : 'Generar diagrama de flujo usando inteligencia artificial para visualizar el proceso'
        const alcanceValido = alcance.length >= 10 ? alcance : 'Aplicable temporalmente para la generación de diagramas con IA'
        const procesoValido = procesoNombre.length >= 3 ? procesoNombre : 'Proceso Temporal para IA'

        // Crear procedimiento temporal con datos válidos
        const tempData = {
          codigo: `TEMP_AI_${Date.now()}`,
          titulo: tituloValido,
          objetivo: objetivoValido,
          alcance: alcanceValido,
          proceso: procesoValido,
          version: '0.0.1',
          gestion_id: gestionId,
          estado: 'borrador',
          responsables: [],
          definiciones: '',
          normatividad: ''
        }

        console.log('🔍 Datos para procedimiento temporal:', tempData)

        // Crear procedimiento temporal
        const tempResponse = await axios.post('/api/procedimientos', tempData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })

        if (tempResponse.data && tempResponse.data.id) {
          const tempId = tempResponse.data.id
          console.log('✅ Procedimiento temporal creado:', tempId)

          // Ahora usar el mismo endpoint que funciona en edición
          const response = await axios.post(
            `/api/procedimientos/${tempId}/generate-diagram-ai`,
            { diagram_type: "mermaid" },
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('sgc_token')}`,
                'Content-Type': 'application/json'
              },
              timeout: 60000
            }
          )

          const generatedCode = response.data.mermaid_code
          if (generatedCode) {
            updateStatusMessage(`✅ Diagrama generado exitosamente: ${generatedCode.length} caracteres`)
            setMermaidCode(generatedCode)
            setActiveTab(1) // Cambiar a vista previa
            updateStatusMessage('✅ Diagrama generado exitosamente con IA')
            setTimeout(() => hideStatusBar(), 3000)
          } else {
            updateStatusMessage('❌ Respuesta sin código Mermaid')
            setError('No se pudo generar el código Mermaid')
          }
        } else {
          throw new Error('No se pudo crear procedimiento temporal')
        }
      } catch (error) {
        console.error('Error en generateWithSmartAI:', error)
        let errorMessage = 'Error al generar diagrama con IA'

        if (error.response?.data?.detail) {
          errorMessage += ': ' + error.response.data.detail
        } else if (error.message) {
          errorMessage += ': ' + error.message
        }

        setError(errorMessage)
        updateStatusMessage(`❌ ${errorMessage}`)
      } finally {
        setAiLoading(false)
      }
    }
  }

  const generateWithAI = async () => {
    if (actividades.length === 0) {
      setError('No hay actividades para generar el diagrama. Agregue actividades primero.')
      return
    }

    // Verificar límite de intentos
    if (aiAttempts >= maxAiAttempts) {
      setError(`Has alcanzado el límite de ${maxAiAttempts} intentos de generación con IA para este procedimiento en esta sesión.`)
      updateStatusMessage(`⚠️ Límite alcanzado: ${aiAttempts}/${maxAiAttempts} intentos`)
      setTimeout(() => hideStatusBar(), 5000)
      return
    }

    // Incrementar contador de intentos
    incrementAiAttempts()

    setAiLoading(true)
    setError('')
    updateStatusMessage(`🤖 Generando diagrama (${aiAttempts + 1}/${maxAiAttempts}) - Puede tomar hasta 60 segundos...`)
    
    try {
      if (procedimientoId) {
        // Modo servidor: procedimiento ya existe en BD
        updateStatusMessage(`🌐 Enviando solicitud al servidor para procedimiento ID: ${procedimientoId}`)
        const response = await axios.post(
          `/api/procedimientos/${procedimientoId}/generate-diagram-ai`,
          { diagram_type: "mermaid" },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('sgc_token')}`,
              'Content-Type': 'application/json'
            },
            timeout: 60000 // 60 segundos para generación de IA
          }
        )
        
        const generatedCode = response.data.mermaid_code
        if (generatedCode) {
          updateStatusMessage(`✅ Código Mermaid recibido: ${generatedCode.length} caracteres`)
          setMermaidCode(generatedCode)
          
          // Cambiar a la pestaña de vista previa para mostrar el resultado
          setActiveTab(1)
          updateStatusMessage('✅ Diagrama generado exitosamente')
          // Ocultar la barra de estado después de 3 segundos
          setTimeout(() => hideStatusBar(), 3000)
        } else {
          updateStatusMessage('❌ Respuesta sin código Mermaid')
          setError('No se pudo generar el código Mermaid')
          setTimeout(() => hideStatusBar(), 5000)
        }
      } else {
        // Modo local: usar el algoritmo inteligente local
        updateStatusMessage('🏠 Modo local: generando con algoritmo inteligente')
        generateSmartLocalDiagram()
        updateStatusMessage('✅ Diagrama generado con algoritmo local')
        setActiveTab(1)
        setTimeout(() => hideStatusBar(), 3000)
      }
      
    } catch (error) {
      console.error('Error generando diagrama con IA:', error)
      
      let errorMessage = 'Error generando diagrama con IA'
      if (error.response?.data?.detail) {
        errorMessage += ': ' + error.response.data.detail
      } else if (error.message) {
        errorMessage += ': ' + error.message
      }
      
      updateStatusMessage(`❌ ${errorMessage}`)
      setError(errorMessage)
      setTimeout(() => hideStatusBar(), 5000)
    } finally {
      setAiLoading(false)
    }
  }

  const generateWithLocalAI = async () => {
    // Generar diagrama inteligente localmente usando las actividades disponibles
    const titulo = procedimientoData?.titulo || 'Nuevo Procedimiento'
    const proceso = procedimientoData?.proceso || 'Proceso'
    
    updateStatusMessage(`📋 Datos del procedimiento: ${titulo}`)
    updateStatusMessage(`📊 Actividades disponibles: ${actividades.length}`)
    
    // Construir prompt similar al backend
    const prompt = `Eres un experto en diagramas de flujo para procedimientos institucionales.

PROCEDIMIENTO: ${titulo}
PROCESO: ${proceso}

ACTIVIDADES EN ORDEN:
${actividades.map((actividad, index) => `${index + 1}. TÍTULO: ${actividad.titulo || `Actividad ${actividad.orden}`}
   DESCRIPCIÓN: ${actividad.descripcion || ''}
   INFORMACIÓN SOPORTE: ${actividad.informacion_soporte || ''}
   INFORMACIÓN GENERADA: ${actividad.informacion_generada || ''}
   RESPONSABLE: ${actividad.responsables?.map(r => r.nombre).join(', ') || ''}
`).join('\n')}

INSTRUCCIONES:
- Genera código Mermaid usando flowchart TD (top-down)
- Analiza el flujo lógico entre actividades
- Incluye nodos de decisión {{}} donde sea apropiado basado en las descripciones
- Usa formas correctas: [] para procesos, {{}} para decisiones, () para inicio/fin
- Conecta las actividades secuencialmente con lógica coherente
- Si hay bifurcaciones lógicas, créalas basándote en el contexto
- Usa nombres de nodos cortos pero descriptivos
- Si detectas palabras clave como "revisar", "verificar", "evaluar", considera crear decisiones
- Si hay múltiples responsables diferentes, considera crear subgrafos por rol

CARACTERÍSTICAS AVANZADAS REQUERIDAS:
- USA COLORES para diferencias tipos de actividades:
  * fill:#e1f5fe para actividades de inicio/entrada
  * fill:#f3e5f5 para actividades de proceso/gestión  
  * fill:#fff3e0 para actividades de revisión/verificación
  * fill:#e8f5e8 para actividades de finalización/salida
  * fill:#ffebee para actividades críticas/decisiones importantes
- AGREGA ICONOS/INDICADORES usando classDef:
  * classDef entrada fill:#e1f5fe,stroke:#0277bd,color:#000
  * classDef proceso fill:#f3e5f5,stroke:#7b1fa2,color:#000
  * classDef revision fill:#fff3e0,stroke:#f57c00,color:#000
  * classDef salida fill:#e8f5e8,stroke:#388e3c,color:#000
  * classDef critico fill:#ffebee,stroke:#d32f2f,color:#000
- INCLUYE subgrafos cuando haya diferentes responsables/roles
- USA etiquetas descriptivas en las conexiones (-->|"texto"| )
- AGREGA al final las definiciones de clases y aplícalas a los nodos correspondientes

ESTRUCTURA EJEMPLO:
flowchart TD
    START([INICIO]):::entrada --> A[Actividad 1]:::proceso
    A --> B{{¿Verificación OK?}}:::revision
    B -->|Sí| C[Continuar]:::proceso
    B -->|No| D[Corregir]:::critico
    D --> A
    C --> END([FIN]):::salida
    
    classDef entrada fill:#e1f5fe,stroke:#0277bd,color:#000
    classDef proceso fill:#f3e5f5,stroke:#7b1fa2,color:#000
    classDef revision fill:#fff3e0,stroke:#f57c00,color:#000
    classDef salida fill:#e8f5e8,stroke:#388e3c,color:#000
    classDef critico fill:#ffebee,stroke:#d32f2f,color:#000

GENERA ÚNICAMENTE EL CÓDIGO MERMAID SIN EXPLICACIONES:`

    try {
      // Llamar al servicio DeepSeek directamente desde el frontend
      updateStatusMessage('🌐 Enviando datos al endpoint local...')
      const response = await axios.post(
        '/api/procedimientos/0/generate-diagram-ai-local', // Nuevo endpoint para datos locales
        {
          titulo: titulo,
          proceso: proceso,
          actividades: actividades,
          diagram_type: "mermaid"
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sgc_token')}`,
            'Content-Type': 'application/json'
          },
          timeout: 60000 // 60 segundos para generación de IA
        }
      )
      
      const generatedCode = response.data.mermaid_code
      if (generatedCode) {
        updateStatusMessage(`✅ Respuesta del endpoint local: ${generatedCode.length} caracteres`)
        setMermaidCode(generatedCode)
        
        // Cambiar a la pestaña de vista previa para mostrar el resultado
        setActiveTab(1)
        updateStatusMessage('✅ Diagrama generado exitosamente')
        setTimeout(() => hideStatusBar(), 3000)
      }
    } catch (error) {
      // Si falla el servicio AI, usar generación inteligente local
      updateStatusMessage(`❌ Error en endpoint local: ${error.message}`)
      updateStatusMessage('🔄 Activando fallback inteligente...')
      generateSmartLocalDiagram()
      updateStatusMessage('✅ Diagrama generado con algoritmo local')
      setActiveTab(1)
      setTimeout(() => hideStatusBar(), 3000)
    }
  }

  const generateSmartLocalDiagram = () => {
    // Generación inteligente local con colores y estilos
    console.log('🧠 Generando diagrama con algoritmo local inteligente...')
    const actividadesOrdenadas = actividades.sort((a, b) => a.orden - b.orden)

    if (actividadesOrdenadas.length === 0) {
      setMermaidCode(`flowchart TD
    START([INICIO]):::entrada --> A[Definir Actividades]:::proceso
    A --> END([FIN]):::salida

    classDef entrada fill:#e1f5fe,stroke:#0277bd,color:#000
    classDef proceso fill:#f3e5f5,stroke:#7b1fa2,color:#000
    classDef salida fill:#e8f5e8,stroke:#388e3c,color:#000`)
      return
    }

    let diagramCode = 'flowchart TD\n    START([INICIO]):::entrada'
    let connections = []

    console.log(`🔧 Procesando ${actividadesOrdenadas.length} actividades...`)

    actividadesOrdenadas.forEach((actividad, index) => {
      const nodeId = `A${index + 1}`
      const titulo = actividad.titulo || `Actividad ${actividad.orden}`
      const descripcion = (actividad.descripcion || '').toLowerCase()

      // Detectar tipo de actividad para aplicar colores
      let claseEstilo = 'proceso'

      if (index === 0) {
        claseEstilo = 'entrada'
      } else if (index === actividadesOrdenadas.length - 1) {
        claseEstilo = 'salida'
      } else if (descripcion.includes('verificar') || descripcion.includes('revisar') ||
                 descripcion.includes('evaluar') || descripcion.includes('validar') ||
                 descripcion.includes('comprobar') || descripcion.includes('controlar')) {
        claseEstilo = 'revision'
      } else if (descripcion.includes('aprobar') || descripcion.includes('rechazar') ||
                 descripcion.includes('decidir') || descripcion.includes('autorizar')) {
        claseEstilo = 'critico'
      }

      // Crear nodo
      const textoNodo = titulo.length > 35 ? titulo.substring(0, 32) + '...' : titulo

      if (claseEstilo === 'critico' && (descripcion.includes('aprobar') || descripcion.includes('decidir'))) {
        diagramCode += `\n    ${nodeId}{${textoNodo}}:::${claseEstilo}`

        const prevNode = index === 0 ? 'START' : `A${index}`
        connections.push(`    ${prevNode} --> ${nodeId}`)

        if (index < actividadesOrdenadas.length - 1) {
          const nextNodeId = `A${index + 2}`
          connections.push(`    ${nodeId} -->|Aprobado| ${nextNodeId}`)
          connections.push(`    ${nodeId} -->|Rechazado| START`)
        } else {
          connections.push(`    ${nodeId} -->|Aprobado| END`)
          connections.push(`    ${nodeId} -->|Rechazado| START`)
        }
      } else {
        diagramCode += `\n    ${nodeId}[${textoNodo}]:::${claseEstilo}`

        const prevNode = index === 0 ? 'START' : `A${index}`
        connections.push(`    ${prevNode} --> ${nodeId}`)
      }
    })

    diagramCode += '\n    END([FIN]):::salida'

    // Agregar conexión final si no hay decisiones al final
    const lastIndex = actividadesOrdenadas.length
    if (lastIndex > 0 && !connections.some(c => c.includes(`A${lastIndex} --> END`))) {
      const lastActivity = actividadesOrdenadas[lastIndex - 1]
      const descripcion = (lastActivity.descripcion || '').toLowerCase()
      const esDecision = descripcion.includes('aprobar') || descripcion.includes('decidir')

      if (!esDecision) {
        connections.push(`    A${lastIndex} --> END`)
      }
    }

    // Combinar el código con conexiones
    diagramCode += '\n\n' + connections.join('\n')

    // Agregar definiciones de clases con colores
    diagramCode += `\n\n    classDef entrada fill:#e1f5fe,stroke:#0277bd,color:#000
    classDef proceso fill:#f3e5f5,stroke:#7b1fa2,color:#000
    classDef revision fill:#fff3e0,stroke:#f57c00,color:#000
    classDef salida fill:#e8f5e8,stroke:#388e3c,color:#000
    classDef critico fill:#ffebee,stroke:#d32f2f,color:#000`

    console.log(`✅ Diagrama inteligente generado: ${diagramCode.split('\n').length} líneas`)
    setMermaidCode(diagramCode)
  }

  const examples = [
    {
      title: 'Flujo Básico',
      code: `flowchart TD
    A[INICIO] --> B[Paso 1]
    B --> C{¿Condición?}
    C -->|SI| D[Paso 2A]
    C -->|NO| E[Paso 2B]
    D --> F[FIN]
    E --> F`
    },
    {
      title: 'Proceso con Decisión',
      code: `flowchart TD
    A[INICIO] --> B[Solicitar acceso]
    B --> C{¿Tiene carnet?}
    C -->|SI| D[Verificar datos]
    C -->|NO| E[Denegar acceso]
    D --> F{¿Datos correctos?}
    F -->|SI| G[Autorizar entrada]
    F -->|NO| E
    G --> H[FIN]
    E --> H`
    },
    {
      title: 'Flujo con Roles',
      code: `flowchart TD
    subgraph "Estudiante"
        A[Presentar carnet]
    end
    subgraph "Vigilante"
        B[Verificar carnet]
        C[Autorizar acceso]
    end
    subgraph "Coordinador"
        D[Evaluar caso]
        E[Decidir acceso]
    end
    A --> B
    B --> C
    B --> D
    D --> E`
    }
  ]

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Editor de Diagrama de Flujo
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1, 
          borderColor: 'divider'
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab icon={<Code />} label="Código" />
            <Tab icon={<Visibility />} label="Vista Previa" />
          </Tabs>
          
          <IconButton 
            onClick={() => setHelpModalOpen(true)}
            color="primary"
            sx={{ mr: 1 }}
          >
            <Help />
          </IconButton>
        </Box>

        {/* Barra de Estado */}
        {showStatusBar && (
          <Box sx={{ 
            backgroundColor: 'primary.main', 
            color: 'primary.contrastText', 
            px: 2, 
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <LinearProgress 
              sx={{ 
                flexGrow: 1, 
                height: 4, 
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'primary.contrastText'
                }
              }} 
            />
            <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
              {statusMessage}
            </Typography>
          </Box>
        )}

        {/* Panel de Código */}
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                startIcon={aiLoading ? <CircularProgress size={16} /> : <SmartToy />}
                onClick={generateWithSmartAI}
                variant="contained"
                size="small"
                disabled={aiLoading || readOnly || actividades.length === 0 || aiAttempts >= maxAiAttempts}
                color={aiAttempts >= maxAiAttempts ? "error" : "secondary"}
              >
                {aiLoading ? 'Generando con IA...' : 
                 aiAttempts >= maxAiAttempts ? `Límite alcanzado (${aiAttempts}/${maxAiAttempts})` :
                 `🤖 Generar con IA (${aiAttempts}/${maxAiAttempts})`}
              </Button>
              <Button
                startIcon={<AutoAwesome />}
                onClick={generateBasicDiagram}
                variant="outlined"
                size="small"
                disabled={aiLoading}
              >
                Generar Básico
              </Button>
              <Button
                startIcon={<Refresh />}
                onClick={renderDiagram}
                variant="outlined"
                size="small"
                disabled={loading || aiLoading}
              >
                Actualizar Vista
              </Button>
              {!readOnly && (
                <Button
                  startIcon={<Save />}
                  onClick={handleSave}
                  variant="contained"
                  size="small"
                  disabled={!mermaidCode.trim() || aiLoading}
                >
                  Guardar
                </Button>
              )}
            </Box>

            {/* Mensaje de éxito del guardado */}
            {saveSuccess && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                ✅ Diagrama guardado exitosamente
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              rows={12}
              value={mermaidCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="Ingrese el código Mermaid aquí..."
              variant="outlined"
              disabled={readOnly}
              sx={{
                fontFamily: 'monospace',
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace'
                }
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {/* Panel de Vista Previa */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ 
              minHeight: 300,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fafafa'
            }}>
              {loading ? (
                <Typography color="text.secondary">
                  Renderizando diagrama...
                </Typography>
              ) : svgContent ? (
                <div 
                  ref={diagramRef}
                  dangerouslySetInnerHTML={{ __html: svgContent }}
                  style={{ maxWidth: '100%', overflow: 'auto' }}
                />
              ) : (
                <Typography color="text.secondary">
                  {mermaidCode ? 'Error en el diagrama' : 'No hay código para mostrar'}
                </Typography>
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

      </Paper>

      {/* Modal de Ayuda */}
      <Dialog 
        open={helpModalOpen} 
        onClose={() => setHelpModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Ayuda del Editor de Diagramas Mermaid
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            🤖 Generación con IA
          </Typography>
          <Typography variant="body2" paragraph>
            • El botón "🤖 Generar con IA" usa DeepSeek AI para crear diagramas inteligentes
          </Typography>
          <Typography variant="body2" paragraph>
            • Analiza las actividades del procedimiento y genera código Mermaid con lógica de flujo
          </Typography>
          <Typography variant="body2" paragraph>
            • Detecta automáticamente puntos de decisión y bifurcaciones
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            📊 Ejemplos de Diagramas
          </Typography>
          
          <Grid container spacing={2}>
            {examples.map((example, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {example.title}
                    </Typography>
                    <TextField
                      multiline
                      rows={6}
                      value={example.code}
                      variant="outlined"
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={{
                        fontSize: '0.8rem',
                        '& .MuiInputBase-input': {
                          fontFamily: 'monospace',
                          fontSize: '0.8rem'
                        }
                      }}
                    />
                    <Button
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setMermaidCode(example.code)
                        setActiveTab(0)
                        setHelpModalOpen(false)
                      }}
                    >
                      Usar este ejemplo
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            📝 Sintaxis Básica de Mermaid
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" paragraph>
              • <code>flowchart TD</code> - Diagrama de flujo de arriba hacia abajo
            </Typography>
            <Typography variant="body2" paragraph>
              • <code>A[Texto]</code> - Rectángulo (proceso)
            </Typography>
            <Typography variant="body2" paragraph>
              • <code>B{'{Texto}'}</code> - Decisión (rombo)
            </Typography>
            <Typography variant="body2" paragraph>
              • <code>C((Texto))</code> - Círculo (inicio/fin)
            </Typography>
            <Typography variant="body2" paragraph>
              • <code>A --{'>'} B</code> - Conexión simple
            </Typography>
            <Typography variant="body2" paragraph>
              • <code>A --{'>'}|Etiqueta| B</code> - Conexión con etiqueta
            </Typography>
            <Typography variant="body2" paragraph>
              • <code>C --{'>'}|SI| D</code> - Respuesta de decisión
            </Typography>
            <Typography variant="body2" paragraph>
              • <code>C --{'>'}|NO| E</code> - Respuesta alternativa
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            💡 Consejos
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" paragraph>
              • Usa nombres cortos pero descriptivos para los nodos
            </Typography>
            <Typography variant="body2" paragraph>
              • Los IDs de nodos deben ser únicos (A, B, C, etc.)
            </Typography>
            <Typography variant="body2" paragraph>
              • Para texto largo, divide en múltiples líneas con <code>&lt;br/&gt;</code>
            </Typography>
            <Typography variant="body2" paragraph>
              • El botón "Actualizar Vista" re-renderiza el diagrama tras editar el código
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setHelpModalOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DiagramEditor